import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "影视剧", to: "/category/drama" },
  { label: "综艺", to: "/category/variety" },
  { label: "原创", to: "/category/original" },
  { label: "动漫", to: "/category/anime" },
  { label: "专题", to: "/topic/featured-drama" },
  { label: "关于", to: "/about" },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const transparent = isHome && !scrolled && !menuOpen;

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    navigate(kw ? `/search?q=${encodeURIComponent(kw)}` : "/search");
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        transparent ? "bg-transparent" : "bg-background-50/95 backdrop-blur border-b border-background-200"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center h-16 md:h-[68px] gap-4">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap cursor-pointer shrink-0">
            {isHome ? (
              <h1 className="text-xl md:text-2xl font-black tracking-tight">
                <span className={transparent ? "text-background-50" : "text-primary-700"}>精品</span>
                <span className={transparent ? "text-secondary-400" : "text-secondary-500"}>视频</span>
              </h1>
            ) : (
              <span className="text-xl md:text-2xl font-black tracking-tight">
                <span className="text-primary-700">精品</span>
                <span className="text-secondary-500">视频</span>
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors ${
                  isActive(item.to)
                    ? transparent
                      ? "text-secondary-300"
                      : "text-primary-600 bg-primary-50"
                    : transparent
                      ? "text-background-100 hover:text-background-50"
                      : "text-foreground-700 hover:text-primary-600 hover:bg-primary-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <form onSubmit={submitSearch} className="hidden md:flex items-center flex-1 max-w-xs ml-auto">
            <div
              className={`flex items-center w-full rounded-full border px-3 h-9 transition-colors ${
                transparent ? "border-background-100/50 bg-primary-950/30" : "border-background-200 bg-background-100"
              }`}
            >
              <i className={`ri-search-line text-sm ${transparent ? "text-background-100" : "text-foreground-400"}`} />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索视频、剧集、综艺"
                className={`flex-1 bg-transparent px-2 text-sm focus:outline-none ${
                  transparent ? "text-background-50 placeholder:text-background-200" : "text-foreground-900 placeholder:text-foreground-400"
                }`}
              />
              <button type="submit" className="sr-only absolute" aria-label="搜索">搜索</button>
            </div>
          </form>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className={`lg:hidden flex items-center justify-center w-10 h-10 rounded-md ml-auto cursor-pointer ${
              transparent ? "text-background-50" : "text-foreground-800"
            }`}
            aria-label="打开菜单"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-2xl`} />
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 px-4 py-4 animate-fadeIn">
          <form onSubmit={submitSearch} className="flex items-center w-full rounded-full border border-background-200 bg-background-100 px-3 h-10 mb-3">
            <i className="ri-search-line text-sm text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频、剧集、综艺"
              className="flex-1 bg-transparent px-2 text-sm focus:outline-none text-foreground-900 placeholder:text-foreground-400"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-3 py-2.5 rounded-md text-sm font-medium cursor-pointer transition-colors ${
                  isActive(item.to)
                    ? "text-primary-600 bg-primary-50"
                    : "text-foreground-700 bg-background-100 hover:bg-primary-50"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}